import * as angular from "angular";
import * as _ from "lodash";
import {SiteFiltersType} from "../../commons/types/site-filters.type";
import {escapeForJsonSearch} from "../../commons/utils/json-search-escape.util";

export interface PlanningRoutesFilterContext {
  filterConfig: any;
}

export class PlanningRoutesHelperService {

  public siteFiltersType: typeof SiteFiltersType = SiteFiltersType;

  /* @ngInject */
  constructor(private $timeout: any) {}

  public initFilters(panel: any) {
    if (!panel.tabsFilterConfig) {
      panel.tabsFilterConfig = {};
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex]) {
      panel.tabsFilterConfig[panel.selectedTabIndex] = {};
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.branchIds]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.branchIds] = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.serviceAreaIds]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.serviceAreaIds] = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.vehicleTypes]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.vehicleTypes] = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.agentSkills]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.agentSkills] = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.labels]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.labels] = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex].resourceIds) {
      panel.tabsFilterConfig[panel.selectedTabIndex].resourceIds = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex].statuses) {
      panel.tabsFilterConfig[panel.selectedTabIndex].statuses = [];
    }
  }

  public filterRouteByFilters(route: any, ctx: PlanningRoutesFilterContext): boolean {
    // Filter by Branches
    if (ctx.filterConfig[this.siteFiltersType.branchIds]?.length) {
      const routeBranchIds = _.get(route, 'resource.branchIds', []);
      const allowedBranchIds = ctx.filterConfig[this.siteFiltersType.branchIds];

      if (!routeBranchIds || _.isEmpty(_.intersection(routeBranchIds, allowedBranchIds))) {
        return false;
      }
    }

    // Filter by Vehicle Type
    if (ctx.filterConfig[this.siteFiltersType.vehicleTypes]?.length) {
      const routeVehicleType = _.get(route, 'resource.vehicleClass');
      if (!routeVehicleType || !ctx.filterConfig[this.siteFiltersType.vehicleTypes].includes(routeVehicleType)) {
        return false;
      }
    }

    // Filter by Skills
    if (ctx.filterConfig[this.siteFiltersType.agentSkills]?.length) {
      const routeSkills = _.get(route, 'resource.allocationSkills', []);
      const hasMatchingSkill = ctx.filterConfig[this.siteFiltersType.agentSkills].some(s => routeSkills.includes(s));
      if (!hasMatchingSkill) {
        return false;
      }
    }

    // Filter by Service Areas
    if (ctx.filterConfig[this.siteFiltersType.serviceAreaIds]?.length) {
      const resourceAreas = route.resource?.areaIds ?? [];
      const activityAreas = route.activities?.flatMap(a => a.address?.areaIds ?? []) ?? [];
      const areaIdsSet = new Set<string>(_.concat(resourceAreas, activityAreas));
      const routeServiceAreas = Array.from(areaIdsSet);
      const hasMatchingServiceArea = ctx.filterConfig[this.siteFiltersType.serviceAreaIds].some(areaId =>
        routeServiceAreas.includes(areaId)
      );

      if (!hasMatchingServiceArea) {
        return false;
      }
    }

    // Filter by Labels
    if (ctx.filterConfig[this.siteFiltersType.labels]?.length) {
      const routeJobs = _.get(route, 'jobs', []);
      const routeLabels = _.uniq(_.compact(_.flatten(
        routeJobs.map(job => _.flatten(_.map(job.packages, 'labels')))
      )));
      const hasMatchingLabel = ctx.filterConfig[this.siteFiltersType.labels].some(label =>
        routeLabels.includes(label)
      );
      if (!hasMatchingLabel) {
        return false;
      }
    }

    // Filter by Resources
    if (ctx.filterConfig.resourceIds?.length) {
      const routeResourceId = _.get(route, 'resource.id');
      if (!routeResourceId || !ctx.filterConfig.resourceIds.includes(routeResourceId)) {
        return false;
      }
    }

    // Filter by Statuses
    if (ctx.filterConfig.statuses?.length && !ctx.filterConfig.statuses.includes(route.status)) {
      return false;
    }

    // Filter by Synced
    const isSynced = ctx.filterConfig[this.siteFiltersType.isSynced];
    if (!_.isUndefined(isSynced) && !_.isEqual(!!route.jobChangesSyncedAt, isSynced)) {
      return false;
    }

    return this.filterByQuery(route, ctx.filterConfig);
  }

  public filterByQuery(route: any, filterConfig: any): boolean {
    return !filterConfig.queryString ? true : _.includes(JSON.stringify(route), escapeForJsonSearch(filterConfig.queryString));
  }

  public sortFilteredRoutes(filteredRoutes: any[], sortBy: string, sortByType: 'asc' | 'desc'): any[] {
    let filteredRoutesCopy = angular.copy(filteredRoutes);
    return _.orderBy(filteredRoutesCopy, [sortBy], [sortByType]);
  }
}

angular
  .module('bringoz.admin.planning')
  .service('planningRoutesHelperService', PlanningRoutesHelperService);
